<script setup lang="ts">
import type { DynamicToolUIPart, ToolUIPart } from 'ai'
import type { HTMLAttributes } from 'vue'
import { computed, isVNode } from 'vue'
import { cn } from '../utils'

export type ToolPart = ToolUIPart | DynamicToolUIPart

interface Props {
  output?: ToolPart['output']
  errorText?: ToolPart['errorText']
  class?: HTMLAttributes['class']
}

const props = defineProps<Props>()

const showOutput = computed(
  () => props.output !== undefined || props.errorText !== undefined
)

const isObjectOutput = computed(
  () =>
    typeof props.output === 'object' &&
    props.output !== null &&
    !isVNode(props.output)
)
const isStringOutput = computed(() => typeof props.output === 'string')

const formattedOutput = computed(() => {
  if (isObjectOutput.value) {
    return JSON.stringify(props.output, null, 2)
  }
  return String(props.output ?? '')
})
</script>

<template>
  <div
    v-if="showOutput"
    :class="cn('space-y-2 p-4', props.class)"
    v-bind="$attrs"
  >
    <h4
      class="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400"
    >
      {{ props.errorText ? 'Error' : 'Result' }}
    </h4>
    <div
      :class="
        cn(
          'max-h-80 overflow-auto rounded-md text-xs [&_table]:w-full',
          props.errorText
            ? 'bg-red-50 p-3 text-red-700 dark:bg-red-400/10 dark:text-red-300'
            : 'bg-gray-50 p-3 text-gray-900 dark:bg-white/5 dark:text-gray-100'
        )
      "
    >
      <div v-if="props.errorText">
        {{ props.errorText }}
      </div>

      <pre
        v-else-if="isObjectOutput || isStringOutput"
        class="whitespace-pre-wrap"
      ><code>{{ formattedOutput }}</code></pre>
      <div v-else>
        {{ props.output }}
      </div>
    </div>
  </div>
</template>
